import { limit as fbLimit, where } from 'firebase/firestore';
import {
  AlertTriangle,
  Bell,
  Boxes,
  Building2,
  CalendarClock,
  ClipboardList,
  Database,
  HeartPulse,
  LayoutDashboard,
  LogOut,
  Menu,
  PlayCircle,
  RefreshCw,
  Settings,
  Upload,
  X,
} from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

import { useAuth } from '../auth/AuthProvider';
import type { AlertDoc, AlertSeverity } from '../lib/types';
import { useCollection } from '../lib/useFirestore';

const NAV = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/runs', label: 'Get Data', icon: PlayCircle, end: false },
  { to: '/review', label: 'Review & Resolve', icon: ClipboardList, end: false },
  { to: '/records', label: 'Properties', icon: Boxes, end: false },
  { to: '/push', label: 'Send to GetLeverage', icon: Upload, end: false },
  { to: '/data', label: 'Data Cleanup', icon: Database, end: false },
  { to: '/data-refresh', label: 'Data Refresh', icon: RefreshCw, end: false },
  { to: '/recheck', label: 'Re-check', icon: CalendarClock, end: false },
  { to: '/health', label: 'Health & Alerts', icon: HeartPulse, end: false },
  { to: '/settings', label: 'Settings', icon: Settings, end: false },
];

/** Worst severity present in a set of alerts (error > warn > info). */
function worstSeverity(alerts: AlertDoc[]): AlertSeverity {
  if (alerts.some((a) => a.severity === 'error')) return 'error';
  if (alerts.some((a) => a.severity === 'warn')) return 'warn';
  return 'info';
}

export function Layout({ children }: { children: ReactNode }) {
  const { user, logOut } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  // Close the mobile drawer whenever the route changes.
  useEffect(() => setDrawerOpen(false), [location.pathname]);
  // Live open-alert feed drives the top-of-page indicator + banner. Filter by status only (an
  // automatic single-field index) and sort/slice client-side, so no composite index is needed.
  const { data: openAlerts } = useCollection<AlertDoc>(
    'alerts',
    [where('status', '==', 'open'), fbLimit(100)],
    [],
  );

  return (
    <div className="flex h-screen overflow-hidden bg-ink-950">
      {/* Mobile drawer backdrop */}
      {drawerOpen && (
        <button
          type="button"
          aria-label="Close menu"
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={() => setDrawerOpen(false)}
        />
      )}
      <aside
        className={`fixed inset-y-0 left-0 z-40 flex w-60 flex-col border-r border-ink-800 bg-ink-900 transition-transform lg:static lg:translate-x-0 ${
          drawerOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center gap-2.5 px-5 py-5">
          <div className="grid h-9 w-9 place-items-center rounded-lg bg-accent-soft/15 text-accent">
            <Building2 size={18} />
          </div>
          <div className="leading-tight">
            <div className="text-sm font-semibold text-slate-100">Tax Intake</div>
            <div className="text-[11px] text-slate-500">Console</div>
          </div>
        </div>
        <nav className="flex-1 space-y-1 px-3 py-2">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-accent-soft/15 text-accent'
                    : 'text-slate-400 hover:bg-ink-800 hover:text-slate-200'
                }`
              }
            >
              <n.icon size={17} />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-ink-800 p-3">
          <div className="flex items-center gap-2.5 px-2 py-2">
            {user?.photoURL ? (
              <img src={user.photoURL} alt="" className="h-7 w-7 rounded-full" />
            ) : (
              <div className="grid h-7 w-7 place-items-center rounded-full bg-ink-700 text-xs text-slate-300">
                {(user?.email ?? '?')[0]?.toUpperCase()}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="truncate text-xs font-medium text-slate-200">
                {user?.displayName ?? 'Signed in'}
              </div>
              <div className="truncate text-[11px] text-slate-500">{user?.email}</div>
            </div>
            <button
              type="button"
              onClick={() => void logOut()}
              title="Sign out"
              aria-label="Sign out"
              className="rounded-md p-1.5 text-slate-400 hover:bg-ink-800 hover:text-slate-200"
            >
              <LogOut size={15} />
            </button>
          </div>
        </div>
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Top bar: mobile drawer toggle (lg:hidden) + the always-visible alert bell (right). */}
        <div className="flex items-center gap-3 border-b border-ink-800 bg-ink-900 px-4 py-2.5">
          <button
            type="button"
            aria-label="Open menu"
            aria-expanded={drawerOpen}
            className="rounded-md p-1.5 text-slate-300 hover:bg-ink-800 lg:hidden"
            onClick={() => setDrawerOpen(true)}
          >
            <Menu size={20} />
          </button>
          <span className="text-sm font-semibold text-slate-100 lg:hidden">Tax Intake</span>
          <div className="ml-auto">
            <AlertBell alerts={openAlerts} />
          </div>
        </div>
        <AlertBanner alerts={openAlerts} />
        <main className="min-h-0 flex-1 overflow-y-auto">{children}</main>
      </div>
    </div>
  );
}

/** Bell in the top bar with a severity-colored count badge; links to the Health page. */
function AlertBell({ alerts }: { alerts: AlertDoc[] }) {
  const count = alerts.length;
  const tone = worstSeverity(alerts);
  const badgeCls =
    tone === 'error'
      ? 'bg-rose-500 text-white'
      : tone === 'warn'
        ? 'bg-amber-400 text-ink-950'
        : 'bg-sky-500 text-white';
  return (
    <NavLink
      to="/health"
      title={count > 0 ? `${count} open pipeline alert(s)` : 'Pipeline health & alerts'}
      className="relative inline-flex items-center rounded-md p-1.5 text-slate-300 hover:bg-ink-800 hover:text-slate-100"
    >
      <Bell size={18} className={count > 0 ? 'text-slate-100' : undefined} />
      {count > 0 && (
        <span
          className={`absolute -right-0.5 -top-0.5 grid min-w-[16px] place-items-center rounded-full px-1 text-[10px] font-semibold leading-none ${badgeCls}`}
        >
          {count > 99 ? '99+' : count}
        </span>
      )}
    </NavLink>
  );
}

/** Slim, dismissible strip under the top bar when open alerts exist. Re-shows when the count grows. */
function AlertBanner({ alerts }: { alerts: AlertDoc[] }) {
  // Baseline count we've dismissed at; a NEW alert (count above the baseline) re-surfaces the strip.
  const [dismissedAt, setDismissedAt] = useState(0);
  const count = alerts.length;
  if (count === 0 || count <= dismissedAt) return null;
  const tone = worstSeverity(alerts);
  const cls =
    tone === 'error'
      ? 'border-rose-500/30 bg-rose-500/10 text-rose-200'
      : 'border-amber-500/30 bg-amber-500/10 text-amber-200';
  return (
    <div className={`flex items-center gap-3 border-b px-4 py-2 text-sm ${cls}`}>
      <AlertTriangle size={15} className="shrink-0" />
      <span>
        {count} open pipeline alert{count > 1 ? 's' : ''} — a scraper, parser, proxy or CAPTCHA may
        need attention.
      </span>
      <Link to="/health" className="font-medium underline underline-offset-2 hover:opacity-80">
        View
      </Link>
      <button
        type="button"
        aria-label="Dismiss"
        className="ml-auto rounded p-0.5 hover:bg-white/10"
        onClick={() => setDismissedAt(count)}
      >
        <X size={14} />
      </button>
    </div>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
  sticky,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  /** Pin the header (and its actions, e.g. a Save button) to the top while the page scrolls.
   *  Opt-in — pages with their own `sticky top-0` table headers must NOT set this (they'd clash). */
  sticky?: boolean;
}) {
  return (
    <div
      className={`flex items-start justify-between gap-4 border-b border-ink-800 px-8 py-5 ${
        sticky ? 'sticky top-0 z-20 bg-ink-950 shadow-lg shadow-black/20' : ''
      }`}
    >
      <div>
        <h1 className="text-xl font-semibold text-slate-100">{title}</h1>
        {subtitle && <p className="mt-0.5 text-sm text-slate-400">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}
